import React, { useMemo, useState } from 'react';
import './RevenueReforecastCard.css';

const pct = value => (Number.isFinite(Number(value)) ? `${(Number(value) * 100).toFixed(1)}%` : 'Not scored');
const money = value => Number(value || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

/**
 * The audit trail for a reforecast decision. It answers the reviewer's first
 * question—what competed, and why did the current method stay or move—without
 * turning a no-change result into an empty card.
 */
const RevenueReforecastCard = ({ artifact }) => {
  const data = artifact?.data || {};
  const reviewed = Array.isArray(data.reviewed) ? data.reviewed : [];
  const roomsMonths = Array.isArray(data.roomsStatPlan?.proposedMonths) ? data.roomsStatPlan.proposedMonths : [];
  const roomsReviewedMonths = Array.isArray(data.roomsStatPlan?.reviewedMonths) ? data.roomsStatPlan.reviewedMonths : [];
  const [expanded, setExpanded] = useState(false);
  const rows = useMemo(() => {
    const ordered = [...reviewed].sort((a, b) => {
      if (a.decision === b.decision) return String(a.account || '').localeCompare(String(b.account || ''));
      return a.decision === 'challenge' ? -1 : 1;
    });
    return expanded ? ordered : ordered.slice(0, 5);
  }, [reviewed, expanded]);
  const changed = reviewed.filter(row => row.decision === 'challenge').length;
  const roomAccountCount = Number(data.roomsStatPlan?.accountCount);
  const reviewedCount = Number.isFinite(Number(data.reviewedCount))
    ? Number(data.reviewedCount)
    : (reviewed.length || (Number.isFinite(roomAccountCount) ? roomAccountCount : 0));
  const retained = reviewedCount - changed;
  const months = Array.isArray(data.inVersionMonths) ? data.inVersionMonths : [];
  const requestedMonths = Number(data.requestedMonths);
  const resolvedMonths = Number.isFinite(Number(data.resolvedMonths))
    ? Number(data.resolvedMonths)
    : roomsReviewedMonths.length;
  const planLimitations = Array.isArray(data.roomsStatPlan?.limitations) ? data.roomsStatPlan.limitations : [];
  const decision = String(data.decision || '').startsWith('retain_current');
  const hasRoomsPlan = roomsMonths.length > 0;

  return (
    <section className="rr-card" aria-label="Revenue method review">
      <div className="rr-decision">
        <span className={`rr-mark ${decision ? 'is-retained' : 'is-change'}`} aria-hidden="true" />
        <div>
          <div className="rr-decision-title">{decision
            ? 'Current forecast retained'
            : hasRoomsPlan
              ? `${roomsMonths.length} monthly Rooms plan${roomsMonths.length === 1 ? '' : 's'} ready for review`
              : `${changed} method change${changed === 1 ? '' : 's'} cleared review`}</div>
          <div className="rr-decision-sub">
            {reviewedCount} account{reviewedCount === 1 ? '' : 's'} reviewed
            {hasRoomsPlan && Number.isFinite(requestedMonths)
              ? <> · {roomsMonths.length} proposed of {requestedMonths} requested{resolvedMonths > 0 && resolvedMonths !== requestedMonths ? <> · {resolvedMonths} version-resolved</> : null}</>
              : hasRoomsPlan && <> · {roomsMonths.length} month{roomsMonths.length === 1 ? '' : 's'}</>}
            {data.comparableCount != null && <> · {data.comparableCount} with scored alternatives</>}
            {!hasRoomsPlan && Number.isFinite(requestedMonths) && requestedMonths > months.length
              ? <> · {requestedMonths} months requested · {months.length} current-version month{months.length === 1 ? '' : 's'}</>
              : !hasRoomsPlan && months.length > 0 && <> · {months.length} open month{months.length === 1 ? '' : 's'}</>}
            {data.historyCutoff && <> · history through {data.historyCutoff}</>}
          </div>
        </div>
        {decision && <span className="rr-retained-count">{retained} kept</span>}
      </div>

      {hasRoomsPlan && (
        <div className="rr-rooms" role="table" aria-label="Monthly Rooms stat plan">
          <div className="rr-rooms-row rr-head" role="row">
            <span role="columnheader">Month</span>
            <span role="columnheader">Room nights</span>
            <span role="columnheader">ADR</span>
            <span role="columnheader">Rooms revenue</span>
            <span role="columnheader">Change</span>
          </div>
          {roomsMonths.map(month => (
            <div className="rr-rooms-row" role="row" key={`${month.versionId}-${month.month}`}>
              <span className="rr-account" role="cell"><strong>{month.month}</strong><small>{month.method}</small></span>
              <span className="rr-score" role="cell">{Number(month.proposed?.occupiedRooms || 0).toLocaleString()}</span>
              <span className="rr-score" role="cell">{money(month.proposed?.adr)}</span>
              <span className="rr-score" role="cell">{money(month.proposed?.roomsRevenue)}</span>
              <span className={`rr-score ${Number(month.delta) >= 0 ? 'is-positive' : 'is-negative'}`} role="cell">
                {Number(month.delta) > 0 ? '+' : ''}{money(month.delta)}
              </span>
            </div>
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <div className="rr-ledger" role="table" aria-label="Compared revenue methods">
          <div className="rr-row rr-head" role="row">
            <span role="columnheader">Account</span>
            <span role="columnheader">Current</span>
            <span role="columnheader">Best alternative</span>
            <span role="columnheader">Holdout WAPE</span>
          </div>
          {rows.map(row => (
            <div className="rr-row" role="row" key={row.chartOfAccountId || row.account}>
              <span className="rr-account" role="cell">
                <strong>{row.account}</strong>
                <small>{row.department || row.accountNumber || ''}</small>
              </span>
              <span role="cell">{row.currentDriver || 'Unassigned'}</span>
              <span role="cell">{row.challenger || 'No scored alternative'}</span>
              <span className="rr-score" role="cell">
                {row.currentWape != null && row.challengerWape != null
                  ? <>{pct(row.currentWape)} <span aria-hidden="true">→</span> {pct(row.challengerWape)}</>
                  : 'Not comparable'}
              </span>
            </div>
          ))}
        </div>
      )}

      {planLimitations.length > 0 && (
        <details className="rr-limitations">
          <summary>
            <span>Planning limitations</span>
            <span className="rr-limitations-count">{planLimitations.length}</span>
          </summary>
          <ul aria-label="Planning limitations">
            {planLimitations.map((limitation, index) => <li key={`${index}-${limitation}`}>{limitation}</li>)}
          </ul>
        </details>
      )}

      <div className="rr-policy">
        <span>Gate</span>
        <span>≥ {data.thresholds?.minHoldoutMonths ?? 2} holdout months</span>
        <span>≥ {pct(data.thresholds?.minAbsoluteWapeImprovement ?? 0.02).replace('.0', '')} WAPE points</span>
        <span>≥ {pct(data.thresholds?.minRelativeWapeImprovement ?? 0.10).replace('.0', '')} relative improvement</span>
        {reviewed.length > 5 && (
          <button type="button" onClick={() => setExpanded(value => !value)}>
            {expanded ? 'Show less' : `Show all ${reviewed.length}`}
          </button>
        )}
      </div>
    </section>
  );
};

export default RevenueReforecastCard;
